import React, { useEffect, useState, useRef } from 'react';
import {
  Toolbar,
  Typography,
  Box,
  CssBaseline,
  TableContainer,
  Paper,
  Button,
  Popover,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { getApplicantsList, approveApplicant, rejectApplicant, resendVerification } from '../api/Applicant';
import { useNavigate } from 'react-router-dom';
import SecureLS from 'secure-ls'

const ls = new SecureLS({encodingType: 'aes'})

const ApplicantList = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedId = useRef(null);
  const [applicants, setApplicants] = useState([]);
  const navigate = useNavigate();
  const [state, setState] = useState('loading');
  const [remarks, setRemarks] = useState('');
  const [showRemarksDialog, setShowRemarksDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const columns = [
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    { field: 'company', headerName: 'Company', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1.5,
      renderCell: (params) => (
        <Typography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: params.row.status === 'Rejected' ? 'error.main' : 'text.primary'
          }}
          title={params.value || 'No remarks'}
        >
          {params.value || 'No remarks'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      filterable: true,
      filterOperators: [
        {
          label: 'Verified',
          value: 'Verified',
          getApplyFilterFn: () => (value, row) => 
            row?.statusColor === 'Approved' && row?.verified === true
        },
        {
          label: 'Approved',
          value: 'Approved',
          getApplyFilterFn: () => (value, row) => 
            row?.statusColor === 'Approved' && row?.verified === false
        },
        {
          label: 'Pending',
          value: 'Pending',
          getApplyFilterFn: () => (value, row) => 
            row?.statusColor === 'Pending'
        },
        {
          label: 'Rejected',
          value: 'Rejected',
          getApplyFilterFn: () => (value, row) => 
            row?.statusColor === 'Rejected'
        },
        {
          label: 'Contains',
          value: 'contains',
          getApplyFilterFn: () => null,
          InputComponent: () => null
        }
      ],
      renderCell: (params) => (
        <Chip
          label={
            params.row.status === 'Approved' && params.row.verified 
              ? 'Verified' 
              : params.row.status
          }
          color={
            params.row.statusColor === 'Pending'
              ? 'warning'
              : params.row.statusColor === 'Approved' && !params.row.verified
              ? 'secondary'
              : params.row.statusColor === 'Approved' && params.row.verified
              ? 'success'
              : 'error'
          }
        />
      ),
    },
    {
      field: 'dateCreated',
      headerName: 'Date Created',
      flex: 1,
      type: 'date',  // Add type specification
      valueFormatter: (params) => {
        console.log(params);
        if (!params) return 'N/A';
        return params.toLocaleString();
      },
      sortingOrder: ['desc', 'asc'],
      sortComparator: (v1, v2) => v1 - v2,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={(e) => handlePopoverOpen(e, params.row.id)}
          disabled={params.row.status === 'Rejected' || params.row.status === 'Verified'}
        >
          Actions
        </Button>
      ),
    },
  ];

  const rows = applicants.map((applicant) => ({
    id: applicant._id,
    firstName: applicant.firstname || 'N/A',
    lastName: applicant.lastname || 'N/A',
    company: applicant.company || 'N/A',
    email: applicant.email || 'N/A',
    remarks: applicant.remarks || '',
    status: applicant.status === 'Approved' && applicant.verified ? 'Verified' : applicant.status || 'Pending',
    statusColor: applicant.status || 'Pending',
    verified: applicant.verified || false,
    dateCreated: applicant.applicationDate ? new Date(applicant.applicationDate) : new Date(),
  }));

  const paginationModel = { page: 0, pageSize: 5 };

  useEffect(() => {
    const jwt = ls.get('jwt');
    if (!jwt) {
        navigate('/login');
        return
    }
    fetchApplicants();
    // check for jwt
  }, [navigate]);

  const fetchApplicants = async () => {
    try {
      setState('loading');
      const { data } = await getApplicantsList()
      console.log(data)
      setApplicants(data);
    } catch (error) {
      console.error(error);
      setState('error');
    } finally {
      setState('success');
    }
  };

  // Handle Popover
  const handlePopoverOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    selectedId.current = id;
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    selectedId.current = null;
  };

  const handleActionClick = (action) => {
    setPendingAction(action);
    setShowRemarksDialog(true);
  };

  const handleRemarksSubmit = async () => {
    setShowRemarksDialog(false);
    await handleAction(pendingAction);
    setRemarks('');
    setPendingAction(null);
  };

  const handleAction = async (action) => {
    try {
      setState('loading');
      switch (action) {
        case 'Approve': 
          await approveApplicant(selectedId.current, remarks);
          fetchApplicants();
          break;
        case 'Reject':
          await rejectApplicant(selectedId.current, remarks);
          fetchApplicants();
          break;
        case 'Resend':
          await resendVerification(selectedId.current);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
      setState('error');
    } finally {
      setState('success');
      handlePopoverClose();
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
        }}
      >
        <Toolbar />
        <Typography variant="h4" gutterBottom>
          Applications
        </Typography>

        {/* Table */}
        <TableContainer component={Paper}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{ 
              pagination: { paginationModel },
              sorting: {
                sortModel: [{ 
                  field: 'dateCreated', 
                  sort: 'desc' 
                }]
              }
            }}
            pageSizeOptions={[5, 10]}
            sx={{ border: 0 }}
            loading={state === 'loading'}
            components={{
              LoadingOverlay: () => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ),
              NoRowsOverlay: () => (
                <>
                {
                  state === 'error' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="h6">Error Fetching Applicants</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="h6">No Applicants Found</Typography>
                    </Box>
                  )
                }
                
                </>
              ),
            }}
          />
        </TableContainer>

        {/* Popover for Actions */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {applicants.map(applicant => {
              if (applicant._id !== selectedId.current) return null;
              
              return (
                <React.Fragment key={applicant._id}>
                  {applicant.status === 'Approved' && !applicant.verified && (
                    <Button
                      variant="contained"
                      color="info"
                      onClick={() => handleAction('Resend')}
                    >
                      Resend Verification Link
                    </Button>
                  )}
                  {applicant.status === 'Pending' && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleActionClick('Approve')}
                    >
                      Approve
                    </Button>
                  )}
                  {applicant.status === 'Pending' && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleActionClick('Reject')}
                    >
                      Reject
                    </Button>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Popover>

        {/* Remarks Dialog */}
        <Dialog open={showRemarksDialog} onClose={() => setShowRemarksDialog(false)}>
          <DialogTitle>
            {pendingAction === 'Approve' ? 'Approval Remarks' : 'Rejection Remarks'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Remarks"
              fullWidth
              multiline
              rows={4}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder={pendingAction === 'Approve' ? 'Enter approval remarks (optional)' : 'Enter rejection remarks (optional)'}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRemarksDialog(false)}>Cancel</Button>
            <Button onClick={handleRemarksSubmit} variant="contained" color="primary">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ApplicantList;
